import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCircleCheck, faBuildingColumns, faFileInvoice, faCopy } from "@fortawesome/free-solid-svg-icons";
import type { Order, OrderStatus } from "../lib/types";

const fmt = (n: number) => (n ?? 0).toLocaleString("ko-KR");

interface Item { id: string; product_id: string; name_snapshot: string; sku_snapshot: string; unit_price: number; quantity: number; line_total: number; }
interface Payment { id: string; method: string; status: string; amount: number; provider: string | null; provider_tid: string | null; paid_at: number | null; created_at: number; }
interface Detail { order: Order; items: Item[]; payment: Payment | null; }

const STATUS: Record<OrderStatus, { text: string; cls: string; step: number }> = {
  pending_payment: { text: "결제 대기", cls: "badge-warning", step: 1 },
  paid:            { text: "결제 완료", cls: "badge",         step: 2 },
  preparing:       { text: "상품 준비", cls: "badge",         step: 3 },
  shipping:        { text: "배송 중",   cls: "badge-success", step: 4 },
  pickup_ready:    { text: "수령 대기", cls: "badge-success", step: 4 },
  completed:       { text: "거래 완료", cls: "badge-success", step: 5 },
  cancelled:       { text: "취소됨",    cls: "badge-danger",  step: 0 },
};

// 가상계좌 안내 (실서비스에서는 결제사 API로 개별 발급, 여기서는 회사 공용 계좌)
const BANK_ACCOUNT = { bank: "국민은행", number: "000-00-000000-00", holder: "(주)전기닷컴" };

export default function OrderDetail() {
  const { id = "" } = useParams();
  const [sp] = useSearchParams();
  const isNew = sp.get("new") === "1";
  const [data, setData] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const { push } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    api.get<Detail>(`/orders/${id}`)
      .then(setData)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    if (!window.confirm("주문을 취소할까요? 재고가 복구되고 여신 잔고가 차감됩니다.")) return;
    try {
      await api.post(`/orders/${id}/cancel`);
      push("주문이 취소되었습니다.", "success");
      load();
    } catch (e: any) {
      push(e.message, "error");
    }
  };

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).then(() => push("복사했습니다.", "success"));
  };

  if (loading) return <main className="container" style={{ padding: "var(--s-8) var(--s-5)" }}><div className="skel" style={{ height: 320 }} /></main>;
  if (err || !data) return (
    <main className="container" style={{ padding: "var(--s-10) var(--s-5)", textAlign: "center" }}>
      <h1 className="h-title">주문을 찾을 수 없습니다</h1>
      <p className="muted">{err}</p>
      <Link to="/orders" className="btn btn-primary" style={{ marginTop: 16 }}>내 주문 내역</Link>
    </main>
  );

  const { order, items, payment } = data;
  const s = STATUS[order.status] ?? STATUS.pending_payment;
  const cancellable = order.status === "pending_payment";

  return (
    <main id="main-content" className="container" style={{ padding: "var(--s-6) var(--s-5)", maxWidth: 900 }}>
      <Link to="/orders" className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}>
        <FontAwesomeIcon icon={faArrowLeft} style={{ width: 12, height: 12 }} /> 주문 내역
      </Link>

      {isNew && (
        <div className="card" style={{ padding: "var(--s-5)", marginBottom: "var(--s-4)", background: "var(--success-soft)", borderLeft: "3px solid var(--success)", display: "flex", gap: "var(--s-3)", alignItems: "center" }}>
          <FontAwesomeIcon icon={faCircleCheck} style={{ width: 28, height: 28, color: "var(--success)" }} />
          <div>
            <div style={{ fontWeight: 700, fontFamily: "var(--font-display)" }}>주문이 접수되었습니다</div>
            <div className="muted" style={{ fontSize: 15 }}>주문번호: <span className="mono">{order.order_no}</span> · 결제 방식에 따른 안내가 아래에 표시됩니다.</div>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: "var(--s-6)", marginBottom: "var(--s-4)" }}>
        <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          <div>
            <div className="eyebrow">주문</div>
            <h1 style={{ fontSize: 22, fontFamily: "var(--font-display)", fontWeight: 700, margin: "4px 0" }} className="mono">{order.order_no}</h1>
            <div className="muted" style={{ fontSize: 15 }}>{new Date(order.created_at * 1000).toLocaleString("ko-KR")}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <span className={`badge ${s.cls}`} style={{ fontSize: 15, padding: "4px 12px" }}>{s.text}</span>
            <div className="muted" style={{ fontSize: 15, marginTop: 8 }}>
              수령: {order.fulfillment === "delivery" ? "배송" : "현장·매장 수령"}
            </div>
          </div>
        </div>

        {/* 진행 단계 */}
        {order.status !== "cancelled" && (
          <div className="steps-bar" style={{ marginTop: 16 }}>
            {["결제 대기", "결제 완료", "상품 준비", order.fulfillment === "delivery" ? "배송 중" : "수령 대기", "거래 완료"].map((label, i) => (
              <div key={label} className={`step ${s.step >= i + 1 ? "is-done" : ""} ${s.step === i + 1 ? "is-current" : ""}`}>
                <div className="step-dot" />
                <div className="step-label">{label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 결제 안내 */}
      {order.payment_method === "bank_transfer" && order.status === "pending_payment" && (
        <div className="card" style={{ padding: "var(--s-5)", marginBottom: "var(--s-4)", borderLeft: "3px solid var(--primary)" }}>
          <div className="row" style={{ gap: 12, alignItems: "flex-start" }}>
            <FontAwesomeIcon icon={faBuildingColumns} style={{ width: 24, height: 24, color: "var(--primary)" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontFamily: "var(--font-display)", marginBottom: 10 }}>계좌이체 안내</div>
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: "8px 16px", fontSize: 15 }}>
                <span className="muted">입금 계좌</span>
                <span><strong>{BANK_ACCOUNT.bank}</strong> <span className="mono">{BANK_ACCOUNT.number}</span> ({BANK_ACCOUNT.holder})</span>
                <button className="btn btn-ghost btn-sm" onClick={() => copy(BANK_ACCOUNT.number)}><FontAwesomeIcon icon={faCopy} style={{ width: 11, height: 11 }} /></button>
                <span className="muted">입금 금액</span>
                <span style={{ fontWeight: 700, color: "var(--accent)" }}>{fmt(order.total)}원</span>
                <button className="btn btn-ghost btn-sm" onClick={() => copy(String(order.total))}><FontAwesomeIcon icon={faCopy} style={{ width: 11, height: 11 }} /></button>
                <span className="muted">입금자명</span>
                <span>주문번호 <span className="mono">{order.order_no}</span></span>
                <span></span>
                <span className="muted">입금 기한</span>
                <span>주문 후 24시간 이내</span>
                <span></span>
              </div>
              <p className="muted" style={{ fontSize: 15, marginTop: 10, lineHeight: 1.6 }}>
                ※ 입금 확인 후 관리자가 결제 완료 처리하며, 완료 시 상품이 준비됩니다.<br />
                ※ 미입금 주문은 24시간 후 자동 취소됩니다.
              </p>
            </div>
          </div>
        </div>
      )}

      {order.payment_method === "tax_invoice" && order.status === "pending_payment" && (
        <div className="card" style={{ padding: "var(--s-5)", marginBottom: "var(--s-4)", borderLeft: "3px solid var(--accent)" }}>
          <div className="row" style={{ gap: 12, alignItems: "flex-start" }}>
            <FontAwesomeIcon icon={faFileInvoice} style={{ width: 24, height: 24, color: "var(--accent)" }} />
            <div>
              <div style={{ fontWeight: 700, fontFamily: "var(--font-display)" }}>세금계산서 후불 거래</div>
              <p className="muted" style={{ fontSize: 15, marginTop: 6, lineHeight: 1.7 }}>
                승인된 여신 한도 내에서 주문이 접수되었습니다. 세금계산서는 월말 일괄 발행되며, 지정 기일까지 납입하시면 됩니다.
              </p>
            </div>
          </div>
        </div>
      )}

      {order.payment_method === "card" && order.status === "pending_payment" && (
        <div className="card" style={{ padding: "var(--s-5)", marginBottom: "var(--s-4)", borderLeft: "3px solid var(--warning)" }}>
          <div style={{ fontWeight: 700, fontFamily: "var(--font-display)" }}>카드결제 대기 중</div>
          <p className="muted" style={{ fontSize: 15, marginTop: 6 }}>
            PG 연동 이전 단계로 관리자가 결제 정보를 확인 후 수동으로 승인합니다.
          </p>
        </div>
      )}

      {/* 주문 항목 */}
      <div className="card" style={{ marginBottom: "var(--s-4)" }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", fontWeight: 700 }}>주문 항목 ({items.length})</div>
        <table>
          <thead><tr><th>SKU</th><th>상품명</th><th style={{ textAlign: "right" }}>단가</th><th style={{ textAlign: "right" }}>수량</th><th style={{ textAlign: "right" }}>합계</th></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <td className="mono" style={{ fontSize: 15 }}>{it.sku_snapshot}</td>
                <td>{it.name_snapshot}</td>
                <td style={{ textAlign: "right" }}>{fmt(it.unit_price)}원</td>
                <td style={{ textAlign: "right" }}>{it.quantity}</td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(it.line_total)}원</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><th colSpan={4} style={{ textAlign: "right" }}>상품 합계</th><td style={{ textAlign: "right" }}>{fmt(order.subtotal)}원</td></tr>
            <tr><th colSpan={4} style={{ textAlign: "right" }}>배송비</th><td style={{ textAlign: "right" }}>{order.shipping_fee === 0 ? "무료" : `${fmt(order.shipping_fee)}원`}</td></tr>
            <tr><th colSpan={4} style={{ textAlign: "right", fontSize: 15 }}>총 결제 금액</th><td style={{ textAlign: "right", fontSize: 18, fontWeight: 800, color: "var(--primary)" }}>{fmt(order.total)}원</td></tr>
          </tfoot>
        </table>
      </div>

      {/* 배송/수령 정보 */}
      <div className="card" style={{ padding: "var(--s-5)", marginBottom: "var(--s-4)" }}>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>{order.fulfillment === "delivery" ? "배송 정보" : "수령 정보"}</div>
        {order.fulfillment === "delivery" ? (
          <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px", fontSize: 15, margin: 0 }}>
            <dt className="muted">받는 분</dt><dd style={{ margin: 0 }}>{order.ship_name ?? "—"}</dd>
            <dt className="muted">연락처</dt><dd style={{ margin: 0 }}>{order.ship_phone ?? "—"}</dd>
            <dt className="muted">주소</dt><dd style={{ margin: 0 }}>{order.ship_address ?? "—"}</dd>
            {order.ship_memo && (<><dt className="muted">메모</dt><dd style={{ margin: 0 }}>{order.ship_memo}</dd></>)}
          </dl>
        ) : (
          <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px", fontSize: 15, margin: 0 }}>
            <dt className="muted">수령 장소</dt><dd style={{ margin: 0 }}>{order.pickup_site ?? "매장 방문"}</dd>
            <dt className="muted">희망일</dt><dd style={{ margin: 0 }}>{order.pickup_date ?? "—"}</dd>
          </dl>
        )}
      </div>

      {/* 결제 요약 */}
      {payment && (
        <div className="card" style={{ padding: "var(--s-5)", marginBottom: "var(--s-4)" }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>결제 정보</div>
          <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px", fontSize: 15, margin: 0 }}>
            <dt className="muted">결제 수단</dt>
            <dd style={{ margin: 0 }}>{payment.method === "card" ? "신용/체크카드" : payment.method === "bank_transfer" ? "계좌이체" : "세금계산서 후불"}</dd>
            <dt className="muted">상태</dt>
            <dd style={{ margin: 0 }}><span className={`badge ${payment.status === "completed" ? "badge-success" : payment.status === "cancelled" ? "badge-danger" : "badge-warning"}`}>{payment.status}</span></dd>
            {payment.provider_tid && (<><dt className="muted">거래 ID</dt><dd className="mono" style={{ margin: 0, fontSize: 15 }}>{payment.provider_tid}</dd></>)}
            {payment.paid_at && (<><dt className="muted">결제 완료</dt><dd style={{ margin: 0 }}>{new Date(payment.paid_at * 1000).toLocaleString("ko-KR")}</dd></>)}
          </dl>
        </div>
      )}

      <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <Link to="/orders" className="btn btn-outline">주문 내역으로</Link>
        {cancellable && (
          <button className="btn btn-ghost" onClick={cancel} style={{ color: "var(--danger)" }}>주문 취소</button>
        )}
      </div>

      <style>{`
        .steps-bar { display: flex; gap: 0; align-items: center; padding-top: 12px; }
        .step { flex: 1; position: relative; text-align: center; padding-top: 20px; }
        .step-dot {
          position: absolute; top: 0; left: 50%; transform: translateX(-50%);
          width: 14px; height: 14px; border-radius: 50%;
          background: var(--bg-muted);
          border: 2px solid var(--border);
        }
        .step.is-done .step-dot { background: var(--success); border-color: var(--success); }
        .step.is-current .step-dot { background: var(--accent); border-color: var(--accent); box-shadow: 0 0 0 4px color-mix(in oklch, var(--accent) 25%, transparent); }
        .step-label { font-size: 11px; font-weight: 600; color: var(--text-muted); }
        .step.is-done .step-label { color: var(--text); }
        .step.is-current .step-label { color: var(--accent); }
        .step + .step::before {
          content: ""; position: absolute; top: 6px; right: 50%; left: -50%; height: 2px;
          background: var(--border);
        }
        .step.is-done + .step::before, .step + .step.is-done::before { background: var(--success); }
      `}</style>
    </main>
  );
}
